import {
  ICurrentOrPast,
  useInfiniteQueryUserLibraryChallenges,
  useInfiniteQueryULChallengesV2,
  useQueryUserLibraryChallenges,
  useQueryUserLibraryChallengesRow,
  useQueryULChallengesRowV2,
  useQueryULChallengesV2,
} from '@ifit/gateway-wolf-athenaeum-service';
import { useEquipmentType } from '../equipment-type';
import { useModality } from '../use-modality';
import getNextPageParam from '../get-next-page';
import {
  useInfiniteQueryOptions,
  useRequiredRequestOptions,
  useRowQueryOptions,
  useMembershipTypeDisabler,
  challengeStoreFF,
} from '../query-options';
import { useFeatureFlags } from '../../../helpers/good-cop-helper';

export const useChallengesRowQuery = (enabled: boolean) => {
  const opts = useRowQueryOptions();
  return useQueryUserLibraryChallengesRow(opts, {
    ...useMembershipTypeDisabler(),
    enabled,
  });
};

export const useChallengesQuery = (
  currentOrPast: ICurrentOrPast,
  enabled: boolean,
) => {
  const opts = useRequiredRequestOptions();
  const params = { ...opts, currentOrPast };
  return useQueryUserLibraryChallenges(params, {
    enabled,
  });
};

export const useInfiniteChallengesQuery = (
  currentOrPast: ICurrentOrPast,
  enabled: boolean,
  perPage?: number,
) => {
  const opts = useInfiniteQueryOptions();
  const params = { ...opts, currentOrPast, perPage };
  return useInfiniteQueryUserLibraryChallenges(params, {
    getNextPageParam,
    enabled,
  });
};

export const useChallengesRowV2Query = (enabled: boolean) => {
  const { equipmentType, isLoading } = useEquipmentType();
  const modality = useModality(equipmentType);
  const opts = {
    ...useRowQueryOptions(),
    modality,
  };
  const isEnabled = !!enabled && !isLoading;
  return useQueryULChallengesRowV2(opts, {
    ...useMembershipTypeDisabler(),
    enabled: isEnabled,
  });
};

export const useChallengesV2Query = (
  currentOrPast: ICurrentOrPast,
  enabled: boolean,
) => {
  const { equipmentType, isLoading } = useEquipmentType();
  const modality = useModality(equipmentType);
  const isEnabled = !!enabled && !isLoading;
  const params = {
    ...useRequiredRequestOptions(),
    modality,
    currentOrPast,
  };
  return useQueryULChallengesV2(params, {
    enabled: isEnabled,
  });
};

export const useInfiniteChallengesV2Query = (
  currentOrPast: ICurrentOrPast,
  enabled: boolean,
  perPage?: number,
) => {
  const { equipmentType, isLoading } = useEquipmentType();
  const modality = useModality(equipmentType);
  const isEnabled = !!enabled && !isLoading;
  const params = {
    ...useInfiniteQueryOptions(),
    modality,
    currentOrPast,
    perPage,
  };
  return useInfiniteQueryULChallengesV2(params, {
    getNextPageParam,
    enabled: isEnabled,
  });
};

export const useGetChallengesQuery = (currentOrPast: ICurrentOrPast) => {
  const featureFlags = useFeatureFlags([challengeStoreFF]);
  const enabled = featureFlags[challengeStoreFF]
    ? (featureFlags[challengeStoreFF] as boolean)
    : false;
  const v2Results = useChallengesV2Query(currentOrPast, enabled);
  const results = useChallengesQuery(currentOrPast, !enabled);
  return enabled ? v2Results : results;
};

export const useGetChallengeRowsQuery = () => {
  const featureFlags = useFeatureFlags([challengeStoreFF]);
  const enabled = featureFlags[challengeStoreFF]
    ? (featureFlags[challengeStoreFF] as boolean)
    : false;
  const v2Results = useChallengesRowV2Query(enabled);
  const results = useChallengesRowQuery(!enabled);
  return enabled ? v2Results : results;
};

export const useGetInfiniteChallengesQuery = (
  currentOrPast: ICurrentOrPast,
  perPage?: number,
) => {
  const featureFlags = useFeatureFlags([challengeStoreFF]);
  const enabled = featureFlags[challengeStoreFF]
    ? (featureFlags[challengeStoreFF] as boolean)
    : false;
  const v2Results = useInfiniteChallengesV2Query(
    currentOrPast,
    enabled,
    perPage,
  );
  const results = useInfiniteChallengesQuery(currentOrPast, !enabled, perPage);
  return enabled ? v2Results : results;
};
